import { useState } from 'react'
import { Key, Plus, RefreshCw, Ban, PlayCircle, ArrowRightLeft, Copy, Check, Loader2, AlertCircle, Shield, Eye, EyeOff } from 'lucide-react'
import { api } from '@/lib/api'

interface Installation {
  installation_id: string; role: string; site_identity: string; status: string; bound_at: string
}
interface License {
  license_id: string; status: string; plan: string; entitlements: string[]; installations: Installation[]
  created_at: string; revoked_at: string | null; transfer_count: number; activated_at: string | null; last_verified_at: string | null
  license_key?: string | null
}

function StatusBadge({ status }: { status: string }) {
  const c: Record<string, string> = {
    active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    revoked: 'bg-red-500/10 text-red-400 border-red-500/20',
    suspended: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${c[status] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'}`}>{status}</span>
}

function RoleBadge({ role }: { role: string }) {
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${role === 'primary' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>{role}</span>
}

export function LicensingPanel() {
  const [licenses, setLicenses] = useState<License[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newKey, setNewKey] = useState<{ key: string; id: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())

  function toggleKeyVisibility(id: string) {
    setVisibleKeys(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next })
  }

  async function loadLicenses() {
    setLoading(true); setError('')
    try {
      const data = await api.listLicenses()
      if (data.success) { setLicenses(data.licenses as License[]); setLoaded(true) }
      else setError('Failed to load licenses')
    } catch (e) { setError('Connection failed: ' + (e instanceof Error ? e.message : '')) }
    setLoading(false)
  }

  async function createLicense() {
    setCreating(true); setError('')
    try {
      const data = await api.createLicense('beta', ['ops.core', 'ops.wordpress'])
      if (data.success) { setNewKey({ key: data.license_key, id: data.license_id }); await loadLicenses() }
      else setError('Failed to create license')
    } catch { setError('Connection failed') }
    setCreating(false)
  }

  async function licenseAction(id: string, action: 'revoke' | 'suspend' | 'reactivate' | 'transfer') {
    setActionLoading(id); setError('')
    try {
      const fn = { revoke: api.revokeLicense, suspend: api.suspendLicense, reactivate: api.reactivateLicense, transfer: api.transferLicense }
      await fn[action](id)
      await loadLicenses()
    } catch { setError(`Failed to ${action}`) }
    setActionLoading(null)
  }

  function copyKey(key: string) { navigator.clipboard.writeText(key); setCopied(true); setTimeout(() => setCopied(false), 2000) }

  if (!loaded) {
    return (
      <div className="p-6 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Key className="w-6 h-6 text-accent" />
          <h1 className="text-xl font-semibold text-text-primary">Licensing</h1>
        </div>
        <p className="text-sm text-text-muted mb-4">Manage OPS Connector Plugin license keys, installations, and entitlements.</p>
        <button onClick={loadLicenses} disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:opacity-90 text-white text-sm font-medium rounded-lg transition disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Load Licenses
        </button>
        {error && <div className="flex items-center gap-2 p-3 mt-4 bg-red-500/5 border border-red-500/10 rounded-lg text-sm text-red-400"><AlertCircle className="w-4 h-4" /> {error}</div>}
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Key className="w-6 h-6 text-accent" />
          <h1 className="text-xl font-semibold text-text-primary">Licensing</h1>
          <span className="text-xs text-text-muted font-mono">{licenses.length} licenses</span>
        </div>
        <div className="flex gap-2">
          <button onClick={loadLicenses} disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-surface-elevated hover:opacity-80 text-text-secondary text-xs rounded-lg transition border border-border">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
          </button>
          <button onClick={createLicense} disabled={creating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent hover:opacity-90 text-white text-xs font-medium rounded-lg transition disabled:opacity-50">
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Create License
          </button>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/5 border border-red-500/10 rounded-lg text-sm text-red-400"><AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}</div>}

      {newKey && (
        <div className="p-4 mb-4 bg-emerald-500/5 border border-emerald-500/15 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-emerald-400" /><span className="text-sm font-medium text-emerald-300">New License Created</span></div>
          <p className="text-xs text-text-muted mb-2">Copy this key now — it will never be shown again.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-2 bg-surface rounded text-xs font-mono text-text-primary break-all select-all">{newKey.key}</code>
            <button onClick={() => copyKey(newKey.key)} className="p-2 bg-surface-elevated hover:opacity-80 rounded-lg transition">
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-text-muted" />}
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="mt-2 text-xs text-text-muted hover:text-text-secondary">Dismiss</button>
        </div>
      )}

      <div className="space-y-3">
        {licenses.map((lic) => (
          <div key={lic.license_id} className="bg-surface border border-border rounded-lg overflow-hidden">
            <div className="p-4 cursor-pointer hover:bg-surface-elevated/50 transition" onClick={() => setExpanded(expanded === lic.license_id ? null : lic.license_id)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Key className="w-4 h-4 text-text-muted" />
                  <code className="text-xs font-mono text-text-secondary">{lic.license_id.slice(0, 8)}…</code>
                  <StatusBadge status={lic.status} />
                  <span className="text-xs text-text-muted font-mono">{lic.plan}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-text-muted">{lic.installations.length} install{lic.installations.length !== 1 ? 's' : ''}</span>
                  <span className="text-xs text-text-muted">{new Date(lic.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            {expanded === lic.license_id && (
              <div className="px-4 pb-4 border-t border-border/50">
                <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
                  <div><span className="text-text-muted block mb-1">License ID</span><code className="text-text-secondary font-mono text-[11px] break-all">{lic.license_id}</code></div>
                  <div><span className="text-text-muted block mb-1">Entitlements</span><div className="flex gap-1 flex-wrap">{lic.entitlements.map((e) => <span key={e} className="px-1.5 py-0.5 bg-accent/10 text-accent rounded text-[10px] font-mono">{e}</span>)}</div></div>
                  <div className="col-span-2">
                    <span className="text-text-muted block mb-1">License Key</span>
                    {lic.license_key ? (
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-1.5 bg-surface-elevated rounded text-[11px] font-mono text-text-secondary break-all select-all">
                          {visibleKeys.has(lic.license_id) ? lic.license_key : '••••••••••••••••••••••••••••••••••••••••••••••••' + lic.license_key.slice(-8)}
                        </code>
                        <button onClick={() => toggleKeyVisibility(lic.license_id)} className="p-1 hover:bg-surface-elevated rounded transition">
                          {visibleKeys.has(lic.license_id) ? <EyeOff className="w-3.5 h-3.5 text-text-muted" /> : <Eye className="w-3.5 h-3.5 text-text-muted" />}
                        </button>
                        <button onClick={() => copyKey(lic.license_key!)} className="p-1 hover:bg-surface-elevated rounded transition">
                          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-text-muted" />}
                        </button>
                      </div>
                    ) : (
                      <span className="text-text-muted text-[11px] italic">Key not available (created before encrypted storage)</span>
                    )}
                  </div>
                  <div><span className="text-text-muted block mb-1">Created</span><span className="text-text-secondary">{new Date(lic.created_at).toLocaleString()}</span></div>
                  <div><span className="text-text-muted block mb-1">Last Verified</span><span className="text-text-secondary">{lic.last_verified_at ? new Date(lic.last_verified_at).toLocaleString() : 'Never'}</span></div>
                </div>
                {lic.installations.length > 0 && (
                  <div className="mt-4">
                    <span className="text-xs text-text-muted block mb-2">Installations</span>
                    <div className="space-y-2">
                      {lic.installations.map((inst) => (
                        <div key={inst.installation_id} className="flex items-center justify-between p-2 bg-surface-elevated/40 rounded-lg">
                          <div className="flex items-center gap-2"><RoleBadge role={inst.role} /><span className="text-xs text-text-secondary font-mono">{inst.site_identity}</span></div>
                          <span className="text-[10px] text-text-muted">{new Date(inst.bound_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 mt-4 pt-3 border-t border-border/30">
                  {lic.status === 'active' && (<>
                    <button onClick={() => licenseAction(lic.license_id, 'suspend')} disabled={actionLoading === lic.license_id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs rounded-md transition border border-amber-500/20"><Ban className="w-3 h-3" /> Suspend</button>
                    <button onClick={() => { if (confirm('Permanently revoke this license?')) licenseAction(lic.license_id, 'revoke') }} disabled={actionLoading === lic.license_id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded-md transition border border-red-500/20"><Ban className="w-3 h-3" /> Revoke</button>
                  </>)}
                  {(lic.status === 'suspended' || lic.status === 'revoked') && (
                    <button onClick={() => licenseAction(lic.license_id, 'reactivate')} disabled={actionLoading === lic.license_id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs rounded-md transition border border-emerald-500/20"><PlayCircle className="w-3 h-3" /> Reactivate</button>
                  )}
                  <button onClick={() => { if (confirm('Transfer? This clears all installations.')) licenseAction(lic.license_id, 'transfer') }} disabled={actionLoading === lic.license_id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-surface-elevated hover:opacity-80 text-text-muted text-xs rounded-md transition border border-border"><ArrowRightLeft className="w-3 h-3" /> Transfer</button>
                  {actionLoading === lic.license_id && <Loader2 className="w-3 h-3 animate-spin text-text-muted" />}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
